import React, { useState, createRef, useEffect, useRef } from 'react';
import { Text, RichText } from '@sitecore-jss/sitecore-jss-react';
import { compose } from 'ramda';
import { useSpring } from 'react-spring';
import { useTranslation } from 'react-i18next';
import { withEditMode, withEditModeNotice } from '_containers/BaseComponent';

import { usePrevious, useWindowSize } from '_utils/hooks';

import { CostAndFeesProps } from './definitions';

import {
  validCostCondition,
  validTab,
  trans,
  getOffsetForCurrentTab,
  getStepElementsChanged,
  isMobSafari
} from './helpers';

import {
  Nav,
  ListWrapper,
  Tablist,
  TabItem,
  TabButton,
  SectionTitle,
  SectionDescription,
  LeftNavButton,
  RightNavButton,
  EditorDiv,
  Section
} from './StyledCostAndFees';

import ContentTabs from './ContentTabs';

const hasWindow = typeof window !== 'undefined';

let Hammer = null;

if (hasWindow) {
  Hammer = require('hammerjs');
}

const ComponentWrapper = compose(withEditModeNotice)(Section);
const EditorText = compose(withEditModeNotice)(EditorDiv);
const listPadding = 12;

const CostAndFees: React.FC<CostAndFeesProps> = ({
  editMode,
  fields: {
    data: { datasource }
  },
  rendering: {
    params: { headingLevel = 'h2' }
  }
}) => {
  const [t] = useTranslation();
  let heading = datasource?.heading;
  let description = datasource?.description;
  let tabs = (datasource?.tabs || [])
    .map((tab) => (
      {
        id: tab.id,
        title: tab.title,
        costConditions: tab.costConditions.filter(validCostCondition)
      }
    ))
    .filter(validTab);

  const isComponentValid = tabs.length > 0;

  // Refs
  const containerRef = useRef<any>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const limits = useRef<any>();
  const hammerRef = useRef<any>();
  const tabElementsRef = useRef(tabs.map(() => createRef()));

  // State
  const [currentTabIndex, setCurrentTabIndex] = useState(0);

  const windowWidth = useWindowSize();

  const [containerWidth, setContainerWidth] = useState(0);
  const [listWidth, setListWidth] = useState(0);
  const [listOffsetX, setListOffsetX] = useState(0);
  const [dragOffsetX, setDragOffsetX] = useState(0);
  const [leftVisible, setLeftVisible] = useState(false);
  const [rightVisible, setRightVisible] = useState(false);
  const [visibleSize, setVisibleSize] = useState(0);
  const [panningActive, setPanningActive] = useState(false);
  const [panningRequired, setPanningRequired] = useState(false);

  const [listSpring, setListSpring] = useSpring(() => ({
    x: 0,
    config: { mass: 1, tension: 180, friction: 20 }
  }));

  const prevActiveTabIndex = usePrevious(currentTabIndex);

  const stepElems = tabElementsRef.current
    ? tabElementsRef.current.map(({ current }) => current)
    : [];

  const prevStepElems = usePrevious(stepElems);

  const stepElemsChanged = getStepElementsChanged(stepElems, prevStepElems);

  // container/list width
  useEffect(() => {
    if (!isComponentValid) {
      return;
    }

    const cw = containerRef.current.offsetWidth - listPadding;
    const lw = listRef.current.offsetWidth;

    setContainerWidth(cw);
    setListWidth(lw);

    if (cw >= lw) {
      limits.current = {
        min: cw / 2 - lw / 2,
        max: cw / 2 - lw / 2
      };
    } else {
      limits.current = {
        min: cw - lw,
        max: 0
      };
    }
  }, [windowWidth, currentTabIndex, isComponentValid]); // currentTabIndex because bold style on active causes list to change size

  // init hammer handlers
  useEffect(() => {
    if (Hammer === null || listRef.current === null) {
      return;
    }

    hammerRef.current = new Hammer(listRef.current, {});

    let deltaX;

    const panEnd = () => {
      const { min, max } = limits.current;
      setListOffsetX((x) => {
        if (x + deltaX < min) {
          return min;
        } else if (x + deltaX > max) {
          return max;
        } else {
          return x + deltaX;
        }
      });
      setDragOffsetX(0);
      setImmediate(() => {
        setPanningActive(false);
      });
    };

    const onHammer = (e) => {
      switch (e.type) {
        case 'panstart':
          setPanningActive(true);
          break;
        case 'pan': {
          if (isMobSafari && e.isFinal) {
            // fixes issue where user drags outside list container and panend is never fired
            panEnd();
          } else {
            deltaX = e.deltaX;
            setDragOffsetX(deltaX);
            e.preventDefault();
          }
          break;
        }
        case 'panend': {
          panEnd();
        }
      }
    };

    hammerRef.current.on('panstart pan panend', onHammer);

    return () => {
      hammerRef.current.off('panstart pan panend', onHammer);
    };
  }, []);

  // update list position spring
  useEffect(() => {
    if (!isComponentValid) {
      return;
    }

    let lx = listOffsetX;
    let dx = dragOffsetX;

    if (!panningRequired) {
      lx = 0;
      dx = 0;
    }

    if ((panningRequired && prevActiveTabIndex !== currentTabIndex) || stepElemsChanged) {
      lx = getOffsetForCurrentTab(tabElementsRef, containerWidth, currentTabIndex, limits);
      dx = 0;
    }

    let visibleLeft = 999999;
    let visibleRight = -999999;

    if (tabElementsRef.current && containerRef.current) {
      const containerRect = containerRef.current.getBoundingClientRect();
      // measure the width of visible step buttons as the basis of how much to scroll
      for (let i = 0; i < tabElementsRef.current.length; i++) {
        const el = tabElementsRef.current[i].current;
        const rect = el.getBoundingClientRect();
        const inBounds = rect.left >= containerRect.left && rect.right <= containerRect.right;

        if (inBounds) {
          visibleLeft = Math.min(visibleLeft, rect.left);
          visibleRight = Math.max(visibleRight, rect.right);
        }
      }
    }

    if (lx < limits.current.min) {
      lx = limits.current.min;
    } else if (listOffsetX > limits.current.max) {
      lx = limits.current.max;
    }

    if (listOffsetX !== lx) {
      setListOffsetX(lx);
    }

    if (dragOffsetX !== dx) {
      setDragOffsetX(dx);
    }

    // computed posn
    let x = lx + dx;

    setLeftVisible(x !== 0);
    setRightVisible(x + listWidth > containerWidth + listPadding);

    setVisibleSize(Math.max(100, visibleRight - visibleLeft));
    setPanningRequired(listWidth > containerWidth + listPadding);

    // update spring
    setListSpring({ x });
  }, [
    listOffsetX,
    dragOffsetX,
    listWidth,
    setListSpring,
    containerWidth,
    prevActiveTabIndex,
    currentTabIndex,
    stepElemsChanged,
    isComponentValid,
    panningRequired
  ]);

  if (datasource === null) {
    if (editMode) {
      return (
        <EditorText
          noticeEnabled
          noticeLevel="warning"
          noticeMessage={`This component needs a datasource to render.`}
        />
      );
    }

    return null;
  }

  if (tabs.length === 0) {
    if (editMode) {
      return (
        <EditorText
          noticeEnabled
          noticeLevel="warning"
          noticeMessage={`No valid tabs available for render. Please set at least 1 Cost and Fee.`}
        />
      );
    }

    return null;
  }

  return (
    <ComponentWrapper
      noticeEnabled={editMode}
      noticeLevel="info"
      noticeMessage={`To configure this component, please use the 'Edit related items' screen.`}
    >
      {heading?.value && (
        <SectionTitle as={headingLevel}>
          <Text field={heading} />
        </SectionTitle>
      )}
      {description?.value && (
        <SectionDescription>
          <RichText field={description} />
        </SectionDescription>
      )}
      <Nav>
        <LeftNavButton
          disabled={panningActive}
          $active={leftVisible}
          aria-hidden={!leftVisible}
          tabIndex={leftVisible ? null : -1}
          type="button"
          title={t('accessibility-previous')}
          aria-label={t('accessibility-previous')}
          onClick={() => {
            setListOffsetX(Math.min(limits.current.max, listOffsetX + visibleSize));
          }}
        />

        <ListWrapper ref={containerRef}>
          <Tablist
            role="tablist"
            ref={listRef}
            style={{ transform: listSpring.x.interpolate(trans) }}
          >
            {tabs.map((tab, i) => {
              const active = currentTabIndex === i;

              return (
                <TabItem key={`tab-${tab.id}`} role="presentation" ref={tabElementsRef.current[i]}>
                  <TabButton
                    disabled={panningActive}
                    type="button"
                    id={`${tab.title.value}-${tab.id}-tab`}
                    onClick={(e) => {
                      setCurrentTabIndex(i);
                    }}
                    role="tab"
                    aria-controls={`${tab.title.value}-${tab.id}-content`}
                    aria-selected={active}
                    $active={active}
                    title={`${t('accessible-tab-label')} ${tab.title.value}`}
                    aria-label={`${t('accessible-tab-label')} ${tab.title.value}`}
                  >
                    {tab.title.value}
                  </TabButton>
                </TabItem>
              );
            })}
          </Tablist>
        </ListWrapper>
        <RightNavButton
          disabled={panningActive}
          $active={rightVisible}
          aria-hidden={!rightVisible}
          tabIndex={rightVisible ? null : -1}
          type="button"
          title={t('accessibility-next')}
          aria-label={t('accessibility-next')}
          onClick={() => {
            setListOffsetX(Math.max(limits.current.min, listOffsetX - visibleSize));
          }}
        />
      </Nav>
      <ContentTabs tabs={tabs} currentTabIndex={currentTabIndex} />
    </ComponentWrapper>
  );
};

export default compose(withEditMode)(CostAndFees);
